interface PostHogConfig {
  api_key?: string
  api_host?: string
}

interface PostHogCurrentUser {
  id?: string
  email?: string
  first_name?: string
  last_name?: string
  organisation_id?: string
  role?: string
}

interface PostHogAppInitData {
  posthog?: PostHogConfig
  current_user?: PostHogCurrentUser
}

interface PostHogBootstrap {
  apiKey: string
  apiHost: string
  distinctId?: string
  personProperties?: Record<string, string | null>
}

interface PostHogInstance {
  capture(eventName: string): void
  identify(distinctId: string, properties?: Record<string, string | null>): void
  reset(): void
}

interface PostHogClient {
  init(
    apiKey: string,
    options: {
      api_host: string
      capture_pageview: false
      defaults: "2026-05-30"
      loaded(instance: PostHogInstance): void
    },
  ): unknown
}

interface InitPostHogOptions {
  appInitData?: PostHogAppInitData | null
  client?: PostHogClient
}

const identityStorageKey = "kambi_posthog_identified"

async function loadPostHogClient(): Promise<PostHogClient> {
  const posthogModule = await import("posthog-js")
  return posthogModule.default
}

function hasStoredIdentity(): boolean {
  try {
    return localStorage.getItem(identityStorageKey) === "true"
  } catch {
    return false
  }
}

function storeIdentity(): void {
  try {
    localStorage.setItem(identityStorageKey, "true")
  } catch {
    // PostHog can still identify the current page when storage is unavailable.
  }
}

function clearStoredIdentity(): void {
  try {
    localStorage.removeItem(identityStorageKey)
  } catch {
    // PostHog can still reset the current page when storage is unavailable.
  }
}

export function readPostHogBootstrapData(): PostHogAppInitData | null {
  const content = document.querySelector<HTMLMetaElement>('meta[name="posthog-bootstrap"]')?.content
  if (!content) return null

  try {
    return JSON.parse(content) as PostHogAppInitData
  } catch {
    return null
  }
}

export function buildPostHogBootstrap(appInitData?: PostHogAppInitData | null): PostHogBootstrap | null {
  const apiKey = appInitData?.posthog?.api_key
  const apiHost = appInitData?.posthog?.api_host
  const currentUser = appInitData?.current_user

  if (!apiKey || !apiHost) return null

  const bootstrap: PostHogBootstrap = {
    apiKey,
    apiHost,
  }

  if (!currentUser?.id) return bootstrap

  return {
    ...bootstrap,
    distinctId: currentUser.id,
    personProperties: {
      email: currentUser.email ?? null,
      first_name: currentUser.first_name ?? null,
      last_name: currentUser.last_name ?? null,
      organisation_id: currentUser.organisation_id ?? null,
      role: currentUser.role ?? null,
      surface: "kambi",
      user_type: "baz_user",
    },
  }
}

export async function initPostHog({
  appInitData = readPostHogBootstrapData(),
  client,
}: InitPostHogOptions = {}): Promise<boolean> {
  const bootstrap = buildPostHogBootstrap(appInitData)
  if (!bootstrap) return false

  const postHogClient = client ?? (await loadPostHogClient())
  const options: Parameters<PostHogClient["init"]>[1] = {
    api_host: bootstrap.apiHost,
    capture_pageview: false,
    // Pin PostHog's defaults so SDK upgrades do not silently change tracking behaviour.
    defaults: "2026-05-30",
    loaded(instance) {
      if (bootstrap.distinctId) {
        instance.identify(bootstrap.distinctId, bootstrap.personProperties)
        storeIdentity()
        instance.capture("$pageview")
      } else if (hasStoredIdentity()) {
        instance.reset()
        clearStoredIdentity()
      }
    },
  }

  postHogClient.init(bootstrap.apiKey, options)
  return true
}
